/**
 * validateExternalUrl — defense-in-depth client-side gate for any
 * user-submitted URL the platform is expected to fetch on the user's
 * behalf (Auto Create from URL, doc-library imports, vendor docs link,
 * webhook targets, etc.).
 *
 * Without this gate, a malicious user could submit URLs that drive the
 * backend to:
 *   1. SSRF into the cloud metadata service (AWS/GCP/Azure 169.254.169.254
 *      → IAM credentials → root account compromise)
 *   2. SSRF into private ranges (RFC 1918 — 10/8, 172.16/12, 192.168/16,
 *      100.64/10 — to scan internal services or hit unsecured admin
 *      panels)
 *   3. Drain credentials embedded in `https://user:pass@…` URLs into
 *      audit logs and access patterns of the fetcher
 *   4. Bypass the fetcher entirely with non-HTTP schemes (`file://`,
 *      `data:`, `javascript:`, `gopher://`, `ftp://`, `dict://`)
 *   5. Loop URLs back at the platform itself (`localhost`, `127.x.x.x`,
 *      `[::1]`, `0.0.0.0`)
 *
 * This module is the FRONTEND gate. The backend MUST run an identical
 * (or stricter) validator on receipt — assume any check here can be
 * bypassed by direct API calls.
 *
 * Exports:
 *   validateExternalUrl(input) → { ok: boolean, reason?: string,
 *                                   normalized?: string, host?: string }
 */

// ── Schemes ───────────────────────────────────────────────────────────
// Only HTTPS is allowed. HTTP is intentionally excluded — we never want
// to enable a downgrade attack on what is effectively a server-side
// fetch from a privileged role.
const ALLOWED_SCHEMES = new Set(['https:']);

// Hard upper bound. The longest legitimate runbook URL we've observed
// in customer data is ~600 chars (deep Confluence path with anchors).
// Capping at 2048 is generous and consistent with the most common
// browser URL limit.
const MAX_LENGTH = 2048;

// ── Hostname blocklists ──────────────────────────────────────────────
// Cloud metadata endpoints — submitting one of these would let the
// fetcher exfiltrate cloud-IAM credentials. Includes the canonical
// AWS/GCP/Azure addresses + the GCP DNS hostname.
const METADATA_HOSTS = new Set([
  '169.254.169.254',                // AWS / GCP / Azure
  'metadata.google.internal',       // GCP
  'metadata',                       // GCP short
  '100.100.100.200',                // Alibaba
  'fd00:ec2::254',                  // AWS IMDS IPv6
  '[fd00:ec2::254]',
]);

// Generic loopback / unspecified.
const LOOPBACK_HOSTS = new Set([
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '::1',
  '[::1]',
]);

// IPv4 dotted-quad shape — used to gate against private ranges below.
const IPV4_RE = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

// ── Helpers ──────────────────────────────────────────────────────────

function isPrivateOrReservedIPv4(host) {
  const m = IPV4_RE.exec(host);
  if (!m) return false;
  const o = m.slice(1).map((s) => Number(s));
  if (o.some((n) => Number.isNaN(n) || n < 0 || n > 255)) return false;
  // 0.0.0.0/8 — current network / unspecified
  if (o[0] === 0) return true;
  // 10.0.0.0/8 — RFC 1918
  if (o[0] === 10) return true;
  // 100.64.0.0/10 — Carrier-Grade NAT
  if (o[0] === 100 && o[1] >= 64 && o[1] <= 127) return true;
  // 127.0.0.0/8 — loopback
  if (o[0] === 127) return true;
  // 169.254.0.0/16 — link-local + cloud metadata
  if (o[0] === 169 && o[1] === 254) return true;
  // 172.16.0.0/12 — RFC 1918
  if (o[0] === 172 && o[1] >= 16 && o[1] <= 31) return true;
  // 192.0.0.0/24 — IETF protocol assignments
  if (o[0] === 192 && o[1] === 0 && o[2] === 0) return true;
  // 192.0.2.0/24 — TEST-NET-1
  if (o[0] === 192 && o[1] === 0 && o[2] === 2) return true;
  // 192.168.0.0/16 — RFC 1918
  if (o[0] === 192 && o[1] === 168) return true;
  // 198.18.0.0/15 — benchmark
  if (o[0] === 198 && (o[1] === 18 || o[1] === 19)) return true;
  // 198.51.100.0/24 — TEST-NET-2
  if (o[0] === 198 && o[1] === 51 && o[2] === 100) return true;
  // 203.0.113.0/24 — TEST-NET-3
  if (o[0] === 203 && o[1] === 0 && o[2] === 113) return true;
  // 224.0.0.0/4 — multicast
  if (o[0] >= 224 && o[0] <= 239) return true;
  // 240.0.0.0/4 — reserved future use
  if (o[0] >= 240) return true;
  return false;
}

function isPrivateOrReservedIPv6(host) {
  // Strip brackets if URL hostname carries them.
  const h = host.replace(/^\[/, '').replace(/\]$/, '').toLowerCase();
  // ::1 loopback
  if (h === '::1' || h === '0:0:0:0:0:0:0:1') return true;
  // :: unspecified
  if (h === '::' || h === '0:0:0:0:0:0:0:0') return true;
  // fc00::/7 — Unique Local Address
  if (/^f[cd][0-9a-f]{2}:/.test(h)) return true;
  // fe80::/10 — link-local
  if (/^fe[89ab][0-9a-f]:/.test(h)) return true;
  // ff00::/8 — multicast
  if (/^ff[0-9a-f]{2}:/.test(h)) return true;
  // IPv4-mapped IPv6 (::ffff:10.0.0.1) — re-check the embedded IPv4.
  const v4MappedMatch = /(?:::ffff:)([\d.]+)$/i.exec(h);
  if (v4MappedMatch) return isPrivateOrReservedIPv4(v4MappedMatch[1]);
  return false;
}

function looksLikeIPv6(host) {
  // Cheap heuristic — any colon in the host means an IPv6 literal.
  return host.includes(':');
}

/**
 * @param {string} input — the raw user input
 * @returns {{ok: true, normalized: string, host: string} | {ok: false, reason: string}}
 */
export function validateExternalUrl(input) {
  if (typeof input !== 'string') {
    return { ok: false, reason: 'URL must be a string.' };
  }
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return { ok: false, reason: 'URL is required.' };
  }
  if (trimmed.length > MAX_LENGTH) {
    return { ok: false, reason: `URL is too long (max ${MAX_LENGTH} chars).` };
  }
  // Reject any whitespace-in-the-middle attempts (hidden CRLF).
  if (/[\s\r\n\t]/.test(trimmed)) {
    return { ok: false, reason: 'URL contains whitespace or control characters.' };
  }
  // Reject obvious unsafe schemes early — even though `URL` would parse
  // some of these, we don't want to spend any cycles inside them.
  if (/^(javascript|data|file|gopher|ftp|dict|jar|view-source):/i.test(trimmed)) {
    return { ok: false, reason: 'Only https:// URLs are allowed.' };
  }
  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch {
    return { ok: false, reason: 'URL is not parseable.' };
  }
  if (!ALLOWED_SCHEMES.has(parsed.protocol)) {
    return { ok: false, reason: `Scheme "${parsed.protocol}" is not allowed — use https://.` };
  }
  // Block embedded credentials. Even if they were the user's own
  // credentials, sending them through a server-side fetcher gets them
  // logged / replicated places they shouldn't be.
  if (parsed.username || parsed.password) {
    return { ok: false, reason: 'URL must not contain user:password credentials.' };
  }
  // Hostname must exist and must not be empty.
  const host = parsed.hostname.toLowerCase();
  if (!host) {
    return { ok: false, reason: 'URL has no hostname.' };
  }
  // Loopback shorthand check first (before IPv4/IPv6 special-casing).
  if (LOOPBACK_HOSTS.has(host)) {
    return { ok: false, reason: 'Loopback addresses are not allowed.' };
  }
  if (METADATA_HOSTS.has(host)) {
    return { ok: false, reason: 'Cloud metadata endpoints are blocked.' };
  }
  // IPv4 / IPv6 private-range checks.
  if (IPV4_RE.test(host)) {
    if (isPrivateOrReservedIPv4(host)) {
      return { ok: false, reason: 'Private, reserved, or link-local IP addresses are not allowed.' };
    }
  } else if (looksLikeIPv6(host)) {
    if (isPrivateOrReservedIPv6(host)) {
      return { ok: false, reason: 'Private, reserved, or link-local IPv6 addresses are not allowed.' };
    }
  } else {
    // Hostname must look like a real hostname (at least one dot, valid
    // label characters). Single-label hostnames like "intranet" can
    // resolve to internal services — block them.
    if (!host.includes('.')) {
      return { ok: false, reason: 'Hostname must include a public TLD (e.g. example.com).' };
    }
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(host)) {
      return { ok: false, reason: 'Hostname contains invalid characters.' };
    }
    // Block .local and .internal pseudo-TLDs frequently used inside
    // corporate networks — fetcher should never reach those.
    if (/\.(local|internal|intranet|corp|home|lan)$/i.test(host)) {
      return { ok: false, reason: 'Internal pseudo-TLDs are blocked.' };
    }
  }
  // Block non-default ports that commonly front internal admin panels
  // (Redis 6379, Mongo 27017, Postgres 5432, etc.). Allow the standard
  // HTTPS port (443) and a handful of common alt-HTTPS ports.
  if (parsed.port) {
    const ALLOWED_PORTS = new Set(['', '443', '8443', '4443', '9443']);
    if (!ALLOWED_PORTS.has(parsed.port)) {
      return { ok: false, reason: `Port ${parsed.port} is not in the allowed set (443, 8443, 4443, 9443).` };
    }
  }
  // Strip the fragment — it's never useful for a server-side fetch and
  // can carry signed-redirect tokens in some IdPs that we don't want
  // bouncing off the fetcher.
  parsed.hash = '';
  // Cap pathname + querystring length so a maliciously huge query
  // string can't slip past the overall length cap by sitting in the
  // tail.
  if (parsed.pathname.length + parsed.search.length > 1024) {
    return { ok: false, reason: 'URL path + query exceeds 1024 chars.' };
  }
  return { ok: true, normalized: parsed.toString(), host };
}

export default validateExternalUrl;
